import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { ShieldCheck, LogOut, RefreshCw, Phone, Mail, CalendarDays, Inbox } from "lucide-react";
import { BookingsTab, JobOrdersTab, CustomersTab, ApplicationsTab, EmployeesTab, PayrollTab, DocumentsTab } from "@/AdminOps";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_LABELS = {
  carpet: "Carpet Cleaning",
  upholstery: "Upholstery & Rug Cleaning",
  detailing: "Mobile Car Detailing",
  landscaping: "Landscaping & Lawn Care",
  maid: "Maid & House Cleaning",
  pressure: "Pressure Washing",
  windows: "Window & Gutter Cleaning",
  multiple: "Multiple Services",
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("epic_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [tab, setTab] = useState("leads");
  const [bookingPrefill, setBookingPrefill] = useState(null);

  const scheduleFromLead = (q) => {
    setBookingPrefill({
      customer_name: q.name,
      phone: q.phone,
      email: q.email,
      service: q.service,
      date: q.preferred_date || "",
      time: "",
      notes: q.details || "",
    });
    setTab("bookings");
  };

  const loadQuotes = useCallback(async (t) => {
    try {
      const { data } = await axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${t}` } });
      setQuotes(data);
      setLastRefresh(new Date());
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("epic_token");
        setToken("");
      }
    }
  }, []);

  const loadEmployees = useCallback(async (t) => {
    try {
      const { data } = await axios.get(`${API}/admin/employees`, { headers: { Authorization: `Bearer ${t}` } });
      setEmployees(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) { loadQuotes(token); loadEmployees(token); }
      } catch {
        localStorage.removeItem("epic_token");
        if (!cancelled) setToken("");
      }
    })();
    const interval = setInterval(() => { loadQuotes(token); loadEmployees(token); }, 15000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [token, loadQuotes, loadEmployees]);

  const onLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("epic_token", data.access_token);
      setToken(data.access_token);
      toast.success("Welcome back, Commander.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onLogout = () => {
    localStorage.removeItem("epic_token");
    setToken("");
    setQuotes([]);
  };

  if (!token) {
    return (
      <div className="epic-app admin-page" data-testid="admin-login-page">
        <Toaster position="top-center" theme="dark" />
        <div className="form-container admin-login-card">
          <ShieldCheck size={44} className="admin-shield" />
          <h2 className="neon-text-pink" data-testid="admin-login-heading">COMMAND CENTER</h2>
          <p className="admin-sub">Owner access only. Identify yourself.</p>
          <form onSubmit={onLogin} data-testid="admin-login-form">
            <input type="email" placeholder="Email Address" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" />
            <input type="password" placeholder="Password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" />
            {error && <p className="admin-error" data-testid="admin-login-error">{error}</p>}
            <button type="submit" className="submit-btn" disabled={loading} data-testid="admin-login-button">
              {loading ? "VERIFYING..." : "LOG IN"}
            </button>
          </form>
          <p className="form-alt"><a href="/" className="neon-text-blue" data-testid="admin-back-link">Back to site</a></p>
        </div>
      </div>
    );
  }

  const TABS = [
    ["leads", "Leads"],
    ["bookings", "Bookings"],
    ["joborders", "Job Orders"],
    ["documents", "Quotes & Invoices"],
    ["customers", "Customers"],
    ["applications", "Applications"],
    ["employees", "Employees"],
    ["payroll", "Payroll"],
  ];

  return (
    <div className="epic-app admin-page" data-testid="admin-inbox-page">
      <Toaster position="top-center" theme="dark" />
      <div className="admin-header">
        <div>
          <h2 className="neon-text-blue" data-testid="admin-inbox-heading">BACK OFFICE</h2>
          <p className="admin-sub" data-testid="admin-inbox-count">
            {quotes.length} lead{quotes.length === 1 ? "" : "s"}
            {lastRefresh ? ` — updated ${lastRefresh.toLocaleTimeString()}` : ""}
          </p>
        </div>
        <div className="admin-header-actions">
          <button className="btn-quote-nav admin-action" onClick={() => { loadQuotes(token); loadEmployees(token); }} data-testid="admin-refresh-button">
            <RefreshCw size={16} /> Refresh
          </button>
          <button className="btn-quote-nav admin-action" onClick={onLogout} data-testid="admin-logout-button">
            <LogOut size={16} /> Log Out
          </button>
        </div>
      </div>
      <div className="admin-tabs" data-testid="admin-tabs">
        {TABS.map(([key, label]) => (
          <button key={key} className={`admin-tab ${tab === key ? "active" : ""}`} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === "leads" && (
        quotes.length === 0 ? (
          <div className="admin-empty" data-testid="admin-inbox-empty">
            <Inbox size={48} className="admin-shield" />
            <p>No mission requests yet. New quotes appear here automatically.</p>
          </div>
        ) : (
          <div className="admin-leads" data-testid="admin-inbox">
            {quotes.map((q) => (
              <div className="lead-card" key={q.id} data-testid={`lead-card-${q.id}`}>
                <div className="lead-top">
                  <span className="lead-service" data-testid={`lead-service-${q.id}`}>{SERVICE_LABELS[q.service] || q.service}</span>
                  <span className="lead-date">{q.created_at ? new Date(q.created_at).toLocaleString() : ""}</span>
                </div>
                <h3 data-testid={`lead-name-${q.id}`}>{q.name}</h3>
                <div className="lead-contacts">
                  <a href={`tel:${q.phone}`} className="lead-contact" data-testid={`lead-phone-${q.id}`}><Phone size={15} /> {q.phone}</a>
                  <a href={`mailto:${q.email}`} className="lead-contact" data-testid={`lead-email-${q.id}`}><Mail size={15} /> {q.email}</a>
                  {q.preferred_date && (
                    <span className="lead-contact" data-testid={`lead-date-pref-${q.id}`}><CalendarDays size={15} /> {q.preferred_date}</span>
                  )}
                </div>
                {q.details && <p className="lead-details" data-testid={`lead-details-${q.id}`}>{q.details}</p>}
                <div className="ops-actions">
                  <button onClick={() => scheduleFromLead(q)} data-testid={`lead-schedule-${q.id}`}>Schedule Booking</button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
      {tab === "bookings" && <BookingsTab token={token} prefill={bookingPrefill} />}
      {tab === "joborders" && <JobOrdersTab token={token} />}
      {tab === "documents" && <DocumentsTab token={token} />}
      {tab === "customers" && <CustomersTab token={token} />}
      {tab === "applications" && <ApplicationsTab token={token} />}
      {tab === "employees" && <EmployeesTab token={token} />}
      {tab === "payroll" && <PayrollTab token={token} employees={employees} />}
    </div>
  );
}
